import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Users, Award, Clock, Phone, Star, ChevronRight, Zap } from "lucide-react";
import { companyInfo, homeServices, stats, advantages, testimonials, heroImages, zones, googleReview } from "../data/mock";

const iconMap = { ShieldCheck, Users, Award, Clock };

const Home = () => {
  return (
    <div>
      {/* HERO */}
      <section className="relative bg-white overflow-hidden">
        <div
          className="h-[560px] md:h-[640px] bg-cover bg-center relative"
          style={{ backgroundImage: `url(${heroImages.home})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/80 to-transparent" />
          <a
            href={`tel:${companyInfo.emergencyPhone.replace(/\s/g, "")}`}
            className="absolute bottom-28 right-6 z-10 hidden sm:flex items-center gap-3 bg-[#123B2E] text-white px-6 py-4 shadow-2xl hover:bg-[#1B4D3C] transition-colors"
            data-testid="hero-photo-phone-badge"
          >
            <Phone className="w-6 h-6 text-[#C9A227]" />
            <div>
              <div className="text-xs text-white/70 uppercase tracking-widest font-bold">Urgence 24h/24</div>
              <div className="text-2xl font-black text-[#C9A227] tabular-nums">{companyInfo.emergencyPhone}</div>
            </div>
          </a>
          <div className="relative max-w-[1400px] mx-auto px-6 h-full flex items-center">
            <div className="max-w-xl">
              <div className="inline-block bg-[#C9A227] text-black text-xs font-bold tracking-widest uppercase px-3 py-1.5 mb-6">
                Serrurier — Spécialiste Fichet
              </div>
              <h1 className="text-4xl md:text-6xl font-black tracking-tight text-black leading-[1.05] mb-4">
                Votre serrure Fichet mérite un vrai spécialiste
              </h1>
              <div className="inline-flex items-center gap-2 bg-[#123B2E] text-[#C9A227] text-sm font-bold px-4 py-2 mb-6" data-testid="depannage-express-badge">
                <Zap className="w-4 h-4" />
                Dépannage express dans toute la commune
              </div>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                {companyInfo.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/devis"
                  className="inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#A8861C] text-black font-bold px-6 py-4 transition-colors group"
                >
                  Demander un devis gratuit
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                </Link>
                <a
                  href={`tel:${companyInfo.emergencyPhone.replace(/\s/g, "")}`}
                  className="inline-flex items-center gap-2 border-2 border-black text-black font-bold px-6 py-4 hover:bg-[#123B2E] hover:text-white transition-colors"
                >
                  <Phone className="w-5 h-5" />
                  Urgence 24/7
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-[#123B2E] text-white py-14">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s, i) => (
            <div key={i} className="text-center border-l-2 border-[#C9A227] pl-4 md:pl-6">
              <div className="text-4xl md:text-5xl font-black text-[#C9A227] tracking-tight">
                {s.value}
                <span className="text-2xl md:text-3xl">{s.suffix}</span>
              </div>
              <div className="text-sm text-gray-300 mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex flex-col md:flex-row items-end justify-between mb-12 gap-4">
            <div className="max-w-2xl">
              <div className="text-sm font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Nos services</div>
              <h2 className="text-3xl md:text-5xl font-black tracking-tight text-black leading-tight">
                Une expertise complète en sécurité
              </h2>
            </div>
            <p className="text-gray-600 max-w-md">
              De l'ouverture de porte à la reproduction de clés, nos techniciens spécialistes de la marque Fichet vous accompagnent à chaque étape.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {homeServices.map((service, i) => (
              <Link
                key={i}
                to={service.path}
                className="group bg-white overflow-hidden hover:shadow-2xl transition-all duration-500"
              >
                <div className="h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                </div>
                <div className="p-7">
                  <h3 className="text-xl font-bold text-black mb-3 tracking-tight group-hover:text-[#8C6F14] transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed mb-5">{service.description}</p>
                  <div className="inline-flex items-center gap-2 text-sm font-bold text-black">
                    En savoir plus
                    <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* REPRODUCTION DE CLE */}
      <section className="py-20 bg-white" data-testid="reproduction-cle-section">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#C9A227] z-0" />
            <img
              src="/reproduction-cle.jpg"
              alt="Reproduction de clé Fichet sur numéro"
              className="relative w-full h-[420px] object-cover z-10"
              data-testid="reproduction-cle-image"
            />
          </div>
          <div>
            <div className="text-sm font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Reproduction de clé</div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-black leading-tight mb-5">
              Reproduction de clé Fichet sur numéro
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Votre clé Fichet est une clé brevetée et protégée contre la copie : sa reproduction n'est possible que sur présentation de votre carte de propriété et du numéro gravé. En tant que spécialistes des serrures Fichet, nous prenons en charge toute la démarche pour vous.
            </p>
            <p className="text-gray-700 leading-relaxed mb-8">
              Nous reproduisons vos clés Fichet à l'identique en usine, dans le respect total du niveau de sécurité de votre cylindre. Clé perdue, clé supplémentaire pour la famille ou remplacement d'un jeu complet : nous vous garantissons une clé d'origine, parfaitement fidèle à votre serrure haute sécurité.
            </p>
            <a
              href={`tel:${companyInfo.emergencyPhone.replace(/\s/g, "")}`}
              className="inline-flex items-center gap-2 bg-[#C9A227] hover:bg-[#A8861C] text-black font-bold px-6 py-4 transition-colors group"
              data-testid="reproduction-cle-call-btn"
            >
              <Phone className="w-5 h-5" />
              Commander votre clé : {companyInfo.emergencyPhone}
            </a>
          </div>
        </div>
      </section>

      {/* ADVANTAGES */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="text-sm font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Pourquoi nous choisir</div>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-black leading-tight">
              Un artisan serrurier à votre service
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((adv, i) => {
              const Icon = iconMap[adv.icon];
              return (
                <div key={i} className="group">
                  <div className="w-14 h-14 bg-[#C9A227] flex items-center justify-center mb-5 group-hover:rotate-6 transition-transform duration-300">
                    <Icon className="w-7 h-7 text-black" />
                  </div>
                  <h3 className="text-lg font-bold text-black mb-2 tracking-tight">{adv.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{adv.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* QUI SOMMES-NOUS */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-[1400px] mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
          <div className="order-2 md:order-1">
            <div className="text-sm font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Qui sommes-nous</div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-black leading-tight mb-5">
              Votre spécialiste des serrures Fichet
            </h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Depuis plus de 10 ans, nous intervenons sur les serrures, cylindres et portes blindées de la marque Fichet. Cette spécialisation nous permet de diagnostiquer et de résoudre rapidement les pannes les plus complexes : serrure multipoints bloquée, clé cassée, cylindre grippé ou porte déréglée.
            </p>
            <p className="text-gray-700 leading-relaxed mb-8">
              Entreprise artisanale, nous ne sommes pas affiliés au groupe Fichet : vous bénéficiez de l'expertise d'un spécialiste, avec la réactivité et les tarifs transparents d'un artisan de proximité.
            </p>
            <Link
              to="/devis"
              className="inline-flex items-center gap-2 bg-[#123B2E] hover:bg-[#1B4D3C] text-white font-bold px-6 py-4 transition-colors group"
            >
              Demander un devis gratuit
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="order-1 md:order-2 relative">
            <div className="absolute -top-4 -left-4 w-32 h-32 bg-[#C9A227] z-0" />
            <img
              src={heroImages.technicienConseil}
              alt="Technicien serrurier spécialiste Fichet"
              className="relative w-full h-[420px] object-cover z-10"
            />
            <a
              href={`tel:${companyInfo.emergencyPhone.replace(/\s/g, "")}`}
              className="absolute bottom-4 right-4 z-20 flex items-center gap-2 bg-[#123B2E] text-white px-4 py-3 shadow-xl hover:bg-[#1B4D3C] transition-colors"
              data-testid="about-photo-phone-badge"
            >
              <Phone className="w-5 h-5 text-[#C9A227]" />
              <span className="text-lg font-black text-[#C9A227] tabular-nums">{companyInfo.emergencyPhone}</span>
            </a>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-14">
            <div className="text-sm font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Témoignages</div>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-black">Ils nous font confiance</h2>
            <div className="inline-flex items-center gap-3 mt-6 bg-gray-50 border border-gray-200 px-6 py-3" data-testid="google-rating-badge">
              <span className="text-2xl font-black">
                <span className="text-[#4285F4]">G</span><span className="text-[#EA4335]">o</span><span className="text-[#FBBC05]">o</span><span className="text-[#4285F4]">g</span><span className="text-[#34A853]">l</span><span className="text-[#EA4335]">e</span>
              </span>
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, j) => (
                  <Star key={j} className="w-5 h-5 fill-[#FBBC05] text-[#FBBC05]" />
                ))}
              </div>
              <span className="font-black text-xl">{googleReview.rating}</span>
              <span className="text-sm text-gray-500">{googleReview.count} avis clients</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <div key={i} className="bg-gray-50 p-8 border-t-4 border-[#C9A227] hover:shadow-lg transition-shadow">
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 fill-[#C9A227] text-[#C9A227]" />
                  ))}
                </div>
                <p className="text-gray-700 leading-relaxed mb-6 italic">« {t.text} »</p>
                <div>
                  <div className="font-bold text-black">{t.name}</div>
                  <div className="text-sm text-gray-500">{t.location}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SIMULATEUR */}
      <section className="py-20 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 grid md:grid-cols-2 gap-10 items-center">
          <div className="relative order-2 md:order-1">
            <div className="absolute -top-4 -left-4 w-32 h-32 bg-[#C9A227] z-0" />
            <div className="relative bg-[#123B2E] p-10 z-10 flex items-center justify-center h-[420px]">
              <div className="w-48 h-72 bg-neutral-800 border-4 border-neutral-900 relative">
                <div className="absolute inset-3 border-2 border-neutral-700" />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 w-1.5 h-16 bg-white/60 rounded-full" />
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[9px] font-bold tracking-widest text-white/50">FICHET</div>
              </div>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <div className="text-sm font-bold text-[#8C6F14] tracking-widest uppercase mb-3">Configurateur 3D</div>
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-black leading-tight mb-5">
              Configurez votre nouvelle porte Fichet
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8">
              Avec notre simulateur, visualisez le rendu de votre nouvelle porte blindée Fichet d'appartement ou de maison. Choisissez le modèle, la couleur, la poignée et le vitrage en temps réel.
            </p>
            <Link
              to="/simulateur"
              className="inline-flex items-center gap-2 bg-[#123B2E] hover:bg-[#1B4D3C] text-white font-bold px-6 py-4 transition-colors group"
            >
              Je configure ma nouvelle porte
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* ZONES D'INTERVENTION */}
      <section className="py-20 bg-[#123B2E]" data-testid="zones-section">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <div className="text-sm font-bold text-[#C9A227] tracking-widest uppercase mb-3">Zones d'intervention</div>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight text-white leading-tight">
              Serrurier Fichet à Paris et en Île-de-France
            </h2>
            <p className="text-white/70 mt-4">
              Nous intervenons rapidement dans Paris et dans toute l'Île-de-France pour vos serrures et portes Fichet.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {zones.map((z) => (
              <div key={z.code} className="bg-white/5 border border-white/10 p-6 hover:border-[#C9A227] transition-colors">
                <div className="w-12 h-12 bg-[#C9A227] flex items-center justify-center font-black text-black text-lg mb-4">
                  {z.code}
                </div>
                <h3 className="font-bold text-white mb-1">{z.name}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{z.cities}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#C9A227] py-16">
        <div className="max-w-[1400px] mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-3xl md:text-4xl font-black text-black tracking-tight">
              Besoin d'un serrurier ? Nous sommes là.
            </h2>
            <p className="text-black/80 mt-2">Devis gratuit et intervention rapide à Paris et en Île-de-France.</p>
          </div>
          <a
            href={`tel:${companyInfo.emergencyPhone.replace(/\s/g, "")}`}
            className="inline-flex items-center gap-3 bg-[#123B2E] text-white font-bold px-8 py-5 hover:bg-[#1B4D3C] transition-colors"
          >
            <Phone className="w-5 h-5" />
            {companyInfo.emergencyPhone}
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;
